import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  enrollmentApi,
  gamificationApi,
  mentorshipApi,
  certificateApi,
} from "../../lib";
import {
  BookOpen,
  Clock,
  Award,
  TrendingUp,
  Video,
  CheckCircle,
  Users,
  Lock,
  Loader,
  MessageCircle,
  ChevronRight,
  Star,
  Flame,
  Target,
  Award as CertificateIcon,
  Brain,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444"];

interface EnrollmentCourse {
  _id: string;
  title: string;
  thumbnail: string;
  shortDescription?: string;
  description?: string;
  difficulty?: string;
  rating?: number;
  price?: number;
  discount?: number;
  finalPrice?: number;
  currency?: string;
  instructorName?: string;
  instructorAvatar?: string;
}

interface Enrollment {
  _id: string;
  user: string;
  course: EnrollmentCourse | null;
  status: "active" | "completed" | "paused" | "cancelled";
  progress: number;
  completedLessons: string[];
  completedModules: string[];
  completedQuizzes: string[];
  lastAccessedLesson?: string;
  enrolledAt: string;
  completedAt?: string;
  expiresAt?: string;
}

const DashboardStudent = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [myBadges, setMyBadges] = useState<any[]>([]);
  const [userStats, setUserStats] = useState<any>(null);
  const [mentorConversations, setMentorConversations] = useState<any[]>([]);
  const [mentorUnreadCount, setMentorUnreadCount] = useState(0);
  const [weeklyLearningHours, setWeeklyLearningHours] = useState<any[]>([]);
  const [totalLearningHours, setTotalLearningHours] = useState(0);
  const [dailyQuests, setDailyQuests] = useState<any[]>([]);
  const [certificates, setCertificates] = useState<any[]>([]);
  const [examStatusMap, setExamStatusMap] = useState<
    Record<
      string,
      { hasCertificateExam: boolean; examPassed: boolean; examAttempts: number }
    >
  >({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          enrollmentsRes,
          badgesRes,
          statsRes,
          conversationsRes,
          questsRes,
          certRes,
        ] = await Promise.all([
          enrollmentApi.getUserEnrollments({ limit: 10 }),
          gamificationApi.getMyBadges(),
          gamificationApi.getMyStats(),
          mentorshipApi
            .getConversations()
            .catch(() => ({ success: false, data: [] })),
          gamificationApi
            .getDailyQuests()
            .catch(() => ({ success: false, data: [] })),
          certificateApi
            .getMyCertificates()
            .catch(() => ({ success: false, data: [] })),
        ]);

        if (enrollmentsRes && enrollmentsRes.success) {
          const items = enrollmentsRes.data || [];
          const map: Record<
            string,
            {
              hasCertificateExam: boolean;
              examPassed: boolean;
              examAttempts: number;
            }
          > = {};
          await Promise.all(
            items.map(async (item: any) => {
              const courseId = item.course?._id;
              if (!courseId) return;
              try {
                const examRes =
                  await enrollmentApi.getCertificateExamStatus(courseId);
                if (examRes?.success) {
                  map[courseId] = examRes.data;
                }
              } catch {
                // skip
              }
            }),
          );
          setEnrollments(items);
          setExamStatusMap(map);
        }
        if (badgesRes && badgesRes.success) {
          setMyBadges(badgesRes.data || []);
        }
        if (statsRes && statsRes.success && statsRes.data) {
          const data = statsRes.data as any;
          setUserStats({
            totalPoints: data.stats?.totalPoints || data.totalPoints || 0,
            level: data.level?.level || data.level || 1,
            streakDays:
              data.stats?.currentStreak || data.stats?.longestStreak || 0,
            badgesCount: data.stats?.badges?.length || 0,
          });
          setWeeklyLearningHours(data.weeklyLearningHours || []);
          setTotalLearningHours(data.totalHoursLearned || 0);
        }
        if (conversationsRes && conversationsRes.success) {
          setMentorConversations(conversationsRes.data?.slice(0, 3) || []);
          const totalUnread =
            conversationsRes.data?.reduce(
              (acc: number, conv: any) => acc + (conv.unreadCount || 0),
              0,
            ) || 0;
          setMentorUnreadCount(totalUnread);
        }
        if (questsRes && questsRes.success) {
          setDailyQuests(questsRes.data || []);
        }
        if (certRes && certRes.success) {
          setCertificates(certRes.data || []);
        }
      } catch (err) {
        console.error("Failed to fetch dashboard data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleContinue = (courseId: string) => {
    if (courseId) {
      navigate(`/course/${courseId}/learn`);
    }
  };

  const handleTakeCertificateExam = (courseId: string) => {
    if (courseId) {
      navigate(`/course/${courseId}/certificate-exam`);
    }
  };

  const stats = [
    {
      label: "Courses Enrolled",
      value: enrollments.length.toString(),
      icon: BookOpen,
      color: "bg-blue-500",
    },
    {
      label: "Hours Learned",
      value: totalLearningHours > 0 ? totalLearningHours.toString() : "0",
      icon: Clock,
      color: "bg-green-500",
    },
    {
      label: "Badges Earned",
      value: myBadges.length.toString(),
      icon: Award,
      color: "bg-purple-500",
    },
    {
      label: "Day Streak",
      value: userStats?.streakDays?.toString() || "0",
      icon: Flame,
      color: "bg-orange-500",
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <Loader className="w-10 h-10 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30 md:py-6">
      <div className="px-4 mx-auto max-w-7xl md:px-6 lg:px-8">
        {/* Header - Fixed Responsive */}
        <div className="flex flex-col gap-3 mb-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg shadow-sm bg-gradient-to-r from-orange-600 to-orange-500 shrink-0 md:w-10 md:h-10">
              <TrendingUp className="w-4 h-4 text-white md:w-5 md:h-5" />
            </div>
            <div>
              <h1 className="text-base font-bold text-gray-900 md:text-xl">
                Welcome back, {user?.firstName || "Student"}! 👋
              </h1>
              <p className="text-xs text-gray-500 md:text-sm">
                Continue your learning journey
              </p>
            </div>
          </div>
          <Link
            to="/dashboard/student/evaluation"
            className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-lg bg-gradient-to-r from-purple-600 to-blue-500 hover:shadow-md transition-all shrink-0 md:px-4 md:py-2 md:text-sm"
          >
            <Brain className="w-3.5 h-3.5 md:w-4 md:h-4" />
            Evaluate Yourself
          </Link>
        </div>

        {/* Stats Grid - Responsive */}
        <div className="grid grid-cols-2 gap-3 mb-5 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
          {stats.map((stat, i) => (
            <div
              key={i}
              className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl md:p-4"
            >
              <div className="flex items-center justify-between mb-1 md:mb-2">
                <div
                  className={`w-8 h-8 ${stat.color} bg-opacity-10 rounded-lg flex items-center justify-center md:w-10 md:h-10`}
                >
                  <stat.icon
                    className={`w-4 h-4 ${stat.color.replace("bg-", "text-")} md:w-5 md:h-5`}
                  />
                </div>
                <span className="text-lg font-bold text-gray-900 md:text-xl">
                  {stat.value}
                </span>
              </div>
              <p className="text-xs text-gray-500 md:text-sm">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 gap-4 mb-5 lg:grid-cols-3 lg:gap-5">
          {/* Course Progress Pie Chart */}
          <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl md:p-5">
            <h2 className="mb-2 text-xs font-semibold text-gray-900 md:text-sm md:mb-3">
              Course Progress
            </h2>
            <div className="h-40 md:h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      {
                        name: "Active",
                        value: enrollments.filter((e) => e.status === "active")
                          .length,
                      },
                      {
                        name: "Completed",
                        value: enrollments.filter(
                          (e) => e.status === "completed",
                        ).length,
                      },
                      {
                        name: "Paused",
                        value: enrollments.filter((e) => e.status === "paused")
                          .length,
                      },
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={25}
                    outerRadius={45}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    <Cell fill="#3B82F6" />
                    <Cell fill="#10B981" />
                    <Cell fill="#F59E0B" />
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Weekly Learning Hours Bar Chart */}
          <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl md:p-5">
            <h2 className="mb-2 text-xs font-semibold text-gray-900 md:text-sm md:mb-3">
              Weekly Learning Hours
            </h2>
            <div className="h-40 md:h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={
                    weeklyLearningHours.length > 0
                      ? weeklyLearningHours
                      : [
                          { day: "Mon", hours: 0 },
                          { day: "Tue", hours: 0 },
                          { day: "Wed", hours: 0 },
                          { day: "Thu", hours: 0 },
                          { day: "Fri", hours: 0 },
                          { day: "Sat", hours: 0 },
                          { day: "Sun", hours: 0 },
                        ]
                  }
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="day"
                    tick={{ fontSize: 9 }}
                    stroke="#9ca3af"
                  />
                  <YAxis tick={{ fontSize: 9 }} stroke="#9ca3af" />
                  <Tooltip />
                  <Bar dataKey="hours" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Learning Stats */}
          <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl md:p-5">
            <h2 className="mb-2 text-xs font-semibold text-gray-900 md:text-sm md:mb-3">
              Learning Stats
            </h2>
            <div className="space-y-2 md:space-y-3">
              <div className="flex items-center justify-between p-2 rounded-lg bg-blue-50 md:p-3">
                <span className="text-xs text-gray-700 md:text-sm">
                  Courses Enrolled
                </span>
                <span className="text-sm font-bold text-blue-600 md:text-base">
                  {enrollments.length || 0}
                </span>
              </div>
              <div className="flex items-center justify-between p-2 rounded-lg bg-green-50 md:p-3">
                <span className="text-xs text-gray-700 md:text-sm">
                  Completed
                </span>
                <span className="text-sm font-bold text-green-600 md:text-base">
                  {enrollments.filter((e) => e.status === "completed").length ||
                    0}
                </span>
              </div>
              <div className="flex items-center justify-between p-2 rounded-lg bg-purple-50 md:p-3">
                <span className="text-xs text-gray-700 md:text-sm">
                  In Progress
                </span>
                <span className="text-sm font-bold text-purple-600 md:text-base">
                  {enrollments.filter((e) => e.status === "active").length || 0}
                </span>
              </div>
              <div className="flex items-center justify-between p-2 rounded-lg bg-orange-50 md:p-3">
                <span className="text-xs text-gray-700 md:text-sm">
                  Avg Progress
                </span>
                <span className="text-sm font-bold text-orange-600 md:text-base">
                  {enrollments.length > 0
                    ? Math.round(
                        enrollments.reduce((acc, e) => acc + e.progress, 0) /
                          enrollments.length,
                      )
                    : 0}
                  %
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          {/* Recent Courses */}
          <div className="space-y-5 lg:col-span-2">
            <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl md:p-5">
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <h2 className="text-xs font-bold text-gray-900 md:text-sm">
                  Continue Learning
                </h2>
                <Link
                  to="/dashboard/student/courses"
                  className="text-[10px] text-orange-600 hover:underline md:text-xs"
                >
                  View All
                </Link>
              </div>
              <div className="space-y-2 md:space-y-3">
                {enrollments.length > 0 ? (
                  enrollments.slice(0, 3).map((enrollment) => {
                    const course = enrollment.course;
                    if (!course) return null;

                    return (
                      <div
                        key={enrollment._id}
                        className="flex gap-2 p-2 transition-colors rounded-lg bg-gray-50 hover:bg-gray-100 md:p-3 md:gap-3"
                      >
                        <div className="flex-shrink-0 w-12 h-12 overflow-hidden bg-gray-200 rounded-lg md:w-16 md:h-16">
                          {course.thumbnail ? (
                            <img
                              src={course.thumbnail}
                              alt={course.title}
                              className="object-cover w-full h-full"
                            />
                          ) : (
                            <div className="flex items-center justify-center w-full h-full">
                              <BookOpen className="w-4 h-4 text-gray-400 md:w-6 md:h-6" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-xs font-semibold text-gray-900 mb-0.5 line-clamp-1 md:text-sm md:mb-1">
                            {course.title || "Untitled Course"}
                          </h3>
                          <div className="flex items-center gap-1.5 mb-0.5 md:gap-2 md:mb-1">
                            <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden md:h-2">
                              <div
                                className="h-full rounded-full bg-gradient-to-r from-orange-500 to-orange-600"
                                style={{
                                  width: `${enrollment.progress || 0}%`,
                                }}
                              />
                            </div>
                            <span className="text-[9px] text-gray-500 md:text-xs">
                              {enrollment.progress || 0}%
                            </span>
                          </div>
                          <p className="text-[9px] text-gray-400 md:text-xs">
                            {enrollment.lastAccessedLesson
                              ? "In progress"
                              : "Start learning"}
                          </p>
                        </div>
                        {course._id && (
                          <>
                            {enrollment.progress >= 100 &&
                              (() => {
                                const hasExam =
                                  course.certificateExam ||
                                  examStatusMap[course._id]?.hasCertificateExam;
                                const examPassed =
                                  !!enrollment.certificateExamPassed ||
                                  !!examStatusMap[course._id]?.examPassed;
                                if (hasExam && !examPassed) {
                                  return (
                                    <button
                                      onClick={() =>
                                        handleTakeCertificateExam(course._id)
                                      }
                                      className="px-2 py-1 text-[9px] font-medium text-white rounded-lg bg-gradient-to-r from-amber-600 to-orange-500 hover:shadow-md whitespace-nowrap md:px-3 md:py-1.5 md:text-xs"
                                    >
                                      Take Exam
                                    </button>
                                  );
                                }
                                return (
                                  <button
                                    onClick={() => handleContinue(course._id)}
                                    className="px-2 py-1 text-[9px] font-medium text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md whitespace-nowrap md:px-3 md:py-1.5 md:text-xs"
                                  >
                                    Review
                                  </button>
                                );
                              })()}
                            {enrollment.progress < 100 && (
                              <button
                                onClick={() => handleContinue(course._id)}
                                className="px-2 py-1 text-[9px] font-medium text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md whitespace-nowrap md:px-3 md:py-1.5 md:text-xs"
                              >
                                Continue
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="py-6 text-center">
                    <BookOpen className="w-10 h-10 mx-auto mb-2 text-gray-300 md:w-12 md:h-12" />
                    <p className="mb-1 text-xs text-gray-500">
                      You haven't enrolled in any courses yet
                    </p>
                    <Link
                      to="/courses"
                      className="text-[10px] text-orange-600 hover:underline md:text-xs"
                    >
                      Browse courses
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Certificates */}
            <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl md:p-5">
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <h2 className="text-xs font-bold text-gray-900 md:text-sm">
                  My Certificates
                </h2>
                <Link
                  to="/dashboard/student/certificates"
                  className="text-[10px] text-orange-600 hover:underline md:text-xs"
                >
                  View All
                </Link>
              </div>
              <div className="space-y-2 md:space-y-3">
                {certificates.length > 0 ? (
                  certificates.slice(0, 3).map((cert: any) => (
                    <Link
                      key={cert._id}
                      to={`/certificate?courseId=${cert.course?._id || cert.course}`}
                      className="flex items-center gap-2 p-2 transition-colors rounded-lg bg-amber-50 hover:bg-amber-100 md:p-3 md:gap-3"
                    >
                      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 text-amber-600 shrink-0 md:w-10 md:h-10">
                        <CertificateIcon className="w-4 h-4 md:w-5 md:h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-gray-900 truncate md:text-sm">
                          {cert.course?.title || "Certificate"}
                        </p>
                        <p className="text-[9px] text-gray-500 truncate md:text-xs">
                          Issued {new Date(cert.issuedAt).toLocaleDateString()}
                        </p>
                      </div>
                      <ChevronRight className="w-3 h-3 text-gray-400 shrink-0 md:w-4 md:h-4" />
                    </Link>
                  ))
                ) : (
                  <div className="py-4 text-center md:py-6">
                    <CertificateIcon className="w-8 h-8 mx-auto mb-1 text-gray-300 md:w-10 md:h-10" />
                    <p className="text-[10px] text-gray-500 mb-0.5 md:text-xs">
                      No certificates yet
                    </p>
                    <p className="text-[9px] text-gray-400 md:text-xs">
                      Complete a course to earn one
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Mentor Messages */}
            <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl md:p-5">
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <h2 className="text-xs font-bold text-gray-900 md:text-sm">
                  Mentor Messages
                </h2>
                <Link
                  to="/dashboard/student/messages"
                  className="text-[10px] text-orange-600 hover:underline md:text-xs"
                >
                  View All
                </Link>
              </div>
              <div className="space-y-2 md:space-y-3">
                {mentorConversations.length > 0 ? (
                  mentorConversations.map((conv: any, i: number) => (
                    <button
                      key={i}
                      onClick={() =>
                        navigate(
                          `/dashboard/student/messages?user=${conv.user._id}`,
                        )
                      }
                      className="flex items-center justify-between w-full p-2 text-left transition-colors rounded-lg bg-orange-50 hover:bg-orange-100 md:p-3"
                    >
                      <div className="flex items-center gap-2 md:gap-3">
                        <div className="flex items-center justify-center w-8 h-8 overflow-hidden bg-orange-100 rounded-full shrink-0 md:w-10 md:h-10">
                          {conv.user?.avatar ? (
                            <img
                              src={conv.user.avatar}
                              alt=""
                              className="object-cover w-full h-full rounded-full"
                            />
                          ) : (
                            <MessageCircle className="w-4 h-4 text-orange-500 md:w-5 md:h-5" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-gray-900 truncate max-w-[100px] md:text-sm md:max-w-[140px]">
                            {conv.user?.name || "Mentor"}
                          </p>
                          <p className="text-[9px] text-gray-500 truncate max-w-[100px] md:text-xs md:max-w-[140px]">
                            {conv.lastMessage || "No messages"}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-0.5">
                        {conv.unreadCount > 0 && (
                          <span className="px-1.5 py-0.5 text-[8px] font-bold text-white bg-orange-500 rounded-full md:px-2 md:py-0.5 md:text-[10px]">
                            {conv.unreadCount}
                          </span>
                        )}
                        {conv.lastMessageAt && (
                          <span className="text-[8px] text-gray-400 md:text-[10px]">
                            {new Date(conv.lastMessageAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="py-4 text-center md:py-6">
                    <MessageCircle className="w-8 h-8 mx-auto mb-1 text-gray-300 md:w-10 md:h-10" />
                    <p className="text-[10px] text-gray-500 mb-0.5 md:text-xs">
                      No messages yet
                    </p>
                    <Link
                      to="/mentorship"
                      className="text-[9px] text-orange-600 hover:underline md:text-xs"
                    >
                      Find a mentor
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-5">
            {/* Badges / Achievements */}
            <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl md:p-5">
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <h2 className="text-xs font-bold text-gray-900 md:text-sm">
                  Badges
                </h2>
                <Link
                  to="/dashboard/badges"
                  className="text-[10px] text-orange-600 hover:underline md:text-xs"
                >
                  View All
                </Link>
              </div>
              <div className="space-y-2 md:space-y-3">
                {myBadges.length > 0 ? (
                  myBadges.slice(0, 4).map((userBadge: any) => (
                    <div
                      key={userBadge._id}
                      className="flex items-center gap-2 p-2 rounded-lg bg-green-50 md:p-3 md:gap-3"
                    >
                      <div className="flex items-center justify-center w-8 h-8 text-base bg-white rounded-lg shadow-sm md:w-10 md:h-10 md:text-xl">
                        {userBadge.badge?.icon || "🏆"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-900 truncate md:text-sm">
                          {userBadge.badge?.name || "Badge"}
                        </p>
                        <p className="text-[9px] text-gray-500 md:text-xs">
                          {userBadge.badge?.points || 0} pts
                        </p>
                      </div>
                      <CheckCircle className="w-3 h-3 text-green-500 shrink-0 md:w-4 md:h-4" />
                    </div>
                  ))
                ) : (
                  <div className="py-4 text-center md:py-6">
                    <Award className="w-8 h-8 mx-auto mb-1 text-gray-300 md:w-10 md:h-10" />
                    <p className="text-[10px] text-gray-500 mb-0.5 md:text-xs">
                      No badges earned yet
                    </p>
                    <Link
                      to="/dashboard/badges"
                      className="text-[9px] text-orange-600 hover:underline md:text-xs"
                    >
                      View available badges
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Level Progress */}
            {userStats && (
              <div className="p-3 border border-orange-100 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl md:p-5">
                <h2 className="mb-2 text-xs font-bold text-gray-900 md:text-sm md:mb-3">
                  Your Level
                </h2>
                <div className="flex items-center justify-between mb-1 md:mb-2">
                  <span className="text-sm font-bold text-orange-600 md:text-base">
                    Level {userStats.level}
                  </span>
                  <span className="text-[10px] text-gray-500 md:text-xs">
                    {userStats.totalPoints} XP
                  </span>
                </div>
                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden mb-2 md:h-2 md:mb-3">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-orange-500 to-orange-600"
                    style={{ width: `${userStats.totalPoints % 100}%` }}
                  />
                </div>
                <p className="text-[9px] text-gray-500 md:text-xs">
                  Keep learning to level up!
                </p>
              </div>
            )}

            {/* Recommended */}
            <div className="p-3 border border-orange-100 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl md:p-5">
              <h2 className="mb-1 text-xs font-bold text-gray-900 md:text-sm md:mb-2">
                Recommended for you
              </h2>
              <p className="text-[9px] text-gray-500 mb-2 md:text-xs md:mb-3">
                Based on your learning history
              </p>
              <div className="space-y-1.5 md:space-y-2">
                {[
                  "Advanced React Patterns",
                  "System Design Interview",
                  "AWS Cloud Practitioner",
                ].map((course, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-[10px] text-gray-700 line-clamp-1 flex-1 md:text-xs">
                      {course}
                    </span>
                    <button className="text-[9px] text-orange-600 hover:underline md:text-xs">
                      View
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Summary Footer */}
        <div className="grid grid-cols-3 gap-2 mt-5 md:gap-3">
          <div className="p-2 text-center bg-white border border-gray-100 shadow-sm rounded-xl md:p-3">
            <BookOpen className="w-4 h-4 mx-auto mb-0.5 text-blue-500 md:w-5 md:h-5 md:mb-1" />
            <p className="text-xs font-bold text-gray-900 md:text-sm">
              {enrollments.filter((e) => e.status === "active").length}
            </p>
            <p className="text-[8px] text-gray-500 md:text-xs">Active</p>
          </div>
          <div className="p-2 text-center bg-white border border-gray-100 shadow-sm rounded-xl md:p-3">
            <Award className="w-4 h-4 mx-auto mb-0.5 text-purple-500 md:w-5 md:h-5 md:mb-1" />
            <p className="text-xs font-bold text-gray-900 md:text-sm">
              {myBadges.length}
            </p>
            <p className="text-[8px] text-gray-500 md:text-xs">Badges</p>
          </div>
          <div className="p-2 text-center bg-white border border-gray-100 shadow-sm rounded-xl md:p-3">
            <Star className="w-4 h-4 mx-auto mb-0.5 text-yellow-500 md:w-5 md:h-5 md:mb-1" />
            <p className="text-xs font-bold text-gray-900 md:text-sm">
              Level {userStats?.level || 1}
            </p>
            <p className="text-[8px] text-gray-500 md:text-xs">Current Level</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardStudent;